/**
 * Compact native action button. Supply label or aria-label describing the action.
 * @element qds-icon-button
 * @attr {string} icon - PrimeIcons or Lucide name; legacy pi-* values remain supported.
 * @attr {string} label - Accessible action name, for example Settings.
 * @attr {string} aria-label - Explicit accessible name, preferred over label.
 * @attr {'primary'|'secondary'|'quiet'|'destructive'} variant - Visual treatment; default quiet.
 * @attr {'sm'|'md'|'lg'} size - Visual size; targets remain at least 44px.
 * @attr {boolean} disabled - Blocks activation.
 * @attr {boolean} loading - Busy state; blocks repeated activation.
 * @attr {'button'|'submit'|'reset'} type - Form action; default button.
 * @attr {string} name - Submitter field name.
 * @attr {string} value - Submitter field value.
 * @attr {string} form - Associated form ID.
 * @attr {string} aria-pressed - Optional toggle state.
 * @attr {string} aria-expanded - Optional disclosure state.
 * @attr {string} aria-controls - Controlled content ID.
 * @csspart button - Native button.
 * @csspart icon - Icon element.
 * @csspart spinner - Decorative busy indicator.
 * @fires click - One cancelable activation with detail { component }.
 */
import { QdsButtonBase, type QdsButtonVariant } from '../shared/button-base.js';
import { adoptPrimeIconsStyles } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons, renderQdsIcon } from '../shared/icon-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
export class QdsIconButton extends QdsButtonBase {
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'icon']; }
  protected override get _iconOnly(): boolean { return true; }
  protected override get _defaultVariant(): QdsButtonVariant { return 'quiet'; }
  protected override get _fallbackName(): string { return this.icon ? `Button with ${this.icon}` : 'Icon button'; }
  get icon(): string { return this.getAttribute('icon') || ''; }
  set icon(value: string) { this.setAttribute('icon', value); }
  override connectedCallback(): void { upgradeProperty(this, 'icon'); super.connectedCallback(); }
  protected override _updateIcon(): void {
    const leading = this.shadowRoot!.querySelector('.leading');
    if (!leading) return;
    leading.innerHTML = renderQdsIcon(this.icon, 'icon', 'icon') || '<i class="pi icon" part="icon" aria-hidden="true"></i>';
    leading.setAttribute('aria-hidden', 'true');
    (this.shadowRoot!.querySelector('slot.label') as HTMLElement).hidden = true;
    if (!this.icon.startsWith('lucide:')) void adoptPrimeIconsStyles(this.shadowRoot!);
    hydrateQdsLucideIcons(this.shadowRoot!);
  }
}
if (!customElements.get('qds-icon-button')) customElements.define('qds-icon-button', QdsIconButton);
